import type {
  MarketingMetric,
  MarketingLeg,
  TrackingLinkDailyMetric,
} from "../types";

// Pure aggregation + formatting helpers for the Marketing / Acquisition page.
// Kept free of React / data-provider so they can be unit-tested in isolation
// (see marketingMetrics.test.ts). The page only ever renders aggregates of the
// `marketing_metrics` rollup — never per-person data — so nothing here touches PII.

export interface CampaignSummary {
  key: string;
  leg: MarketingLeg;
  source: string;
  campaign_token: string;
  ad_id: string;
  label: string;
  acquisition_group: "paid" | "organic" | "unknown";
  medium: string;
  platform: string;
  surface: string;
  campaign_id: string;
  web_pageviews: number;
  web_visitors: number;
  wa_cta_clicks: number;
  short_link_clicks: number;
  new_contacts: number;
  echoes_started: number;
  echoes_completed: number;
  purchases: number;
  spend_cents: number | null;
  impressions: number | null;
  link_clicks: number | null;
  conversations_started: number | null;
}

export interface FunnelTotals {
  web_visitors: number;
  wa_cta_clicks: number;
  short_link_clicks: number;
  new_contacts: number;
  echoes_completed: number;
  purchases: number;
  spend_cents: number | null;
}

export interface DailyBucket {
  period: string;
  website: number;
  ctwa: number;
  organic: number;
  snap: number;
  link: number;
  launchpad: number;
  my_web: number;
}

export interface ContinentSummary {
  code: string;
  label: string;
  new_contacts: number;
  echoes_completed: number;
}

/** The rollup should refresh daily; older Marketing data is flagged on the page. */
export const MARKETING_STALE_AFTER_HOURS = 26;

/**
 * Newest `computed_at` among the rows, or null when none carries one. Every
 * row of a full rollup refresh is stamped with that run's start time.
 */
export function latestComputedAt(
  rows: ReadonlyArray<Pick<MarketingMetric, "computed_at">>,
): string | null {
  let latest: string | null = null;
  let latestMs = -Infinity;
  for (const row of rows) {
    const ms = row.computed_at ? Date.parse(row.computed_at) : NaN;
    if (Number.isFinite(ms) && ms > latestMs) {
      latest = row.computed_at ?? null;
      latestMs = ms;
    }
  }
  return latest;
}

/** True when `iso` is older than `hours` before `now`, or missing/unparseable. */
export function isStale(
  iso: string | null | undefined,
  now: number | Date,
  hours = MARKETING_STALE_AFTER_HOURS,
): boolean {
  const ageMs = new Date(now).getTime() - (iso ? Date.parse(iso) : NaN);
  if (!Number.isFinite(ageMs)) return true;
  return ageMs > hours * 3_600_000;
}

/** Geo (by-continent) rows are stored alongside day funnel rows under granularity='geo'. */
export function isGeoRow(row: Pick<MarketingMetric, "granularity">): boolean {
  return row.granularity === "geo";
}

/** Map exact tracking-link ledger activity into the Marketing row contract. */
export function operationalLinkMarketingRows(
  rows: readonly TrackingLinkDailyMetric[],
): MarketingMetric[] {
  return rows.map((row) => ({
    id: `tracking-link:${row.id}`,
    period: row.period,
    granularity: "day",
    leg: "link",
    source: `link:${row.public_code}`,
    campaign_token: row.public_code,
    ad_id: "",
    campaign_name: row.name,
    web_pageviews: 0,
    web_visitors: 0,
    wa_cta_clicks: 0,
    short_link_clicks: row.short_link_clicks ?? 0,
    new_contacts: row.new_contacts ?? 0,
    echoes_started: row.echoes_started ?? 0,
    echoes_completed: row.echoes_completed ?? 0,
    purchases: 0,
    spend_cents: null,
    impressions: null,
    link_clicks: null,
    conversations_started: null,
    computed_at: row.computed_at ?? undefined,
  }));
}

/**
 * Replace legacy rollup link rows instead of adding to them. The operational
 * ledger is authoritative for tracking links and includes returning users;
 * retaining both sources would double count the same click/conversion.
 */
export function replaceOperationalLinkRows(
  marketingRows: readonly MarketingMetric[],
  operationalRows: readonly TrackingLinkDailyMetric[],
): MarketingMetric[] {
  return [
    ...marketingRows.filter((row) => row.leg !== "link"),
    ...operationalLinkMarketingRows(operationalRows),
  ];
}

/** Group geo rows into one summary per continent (summed across legs). */
export function aggregateByContinent(
  rows: readonly MarketingMetric[],
): ContinentSummary[] {
  const byCode = new Map<string, ContinentSummary>();
  for (const row of rows) {
    const code = row.campaign_token || "UNKNOWN";
    let summary = byCode.get(code);
    if (!summary) {
      summary = {
        code,
        label: row.campaign_name || code,
        new_contacts: 0,
        echoes_completed: 0,
      };
      byCode.set(code, summary);
    }
    if (row.campaign_name) summary.label = row.campaign_name;
    summary.new_contacts += row.new_contacts ?? 0;
    summary.echoes_completed += row.echoes_completed ?? 0;
  }
  return Array.from(byCode.values()).sort(
    (a, b) =>
      b.echoes_completed - a.echoes_completed ||
      b.new_contacts - a.new_contacts,
  );
}

const COUNT_FIELDS = [
  "web_pageviews",
  "web_visitors",
  "wa_cta_clicks",
  "short_link_clicks",
  "new_contacts",
  "echoes_started",
  "echoes_completed",
  "purchases",
] as const;

const NULLABLE_FIELDS = [
  "spend_cents",
  "impressions",
  "link_clicks",
  "conversations_started",
] as const;

/** Stable grouping key for a campaign across periods. */
export function campaignKey(
  m: Pick<
    MarketingMetric,
    | "source"
    | "campaign_token"
    | "ad_id"
    | "acquisition_group"
    | "medium"
    | "platform"
    | "surface"
    | "campaign_id"
  >,
): string {
  return [
    m.source,
    m.campaign_token ?? "",
    m.ad_id ?? "",
    m.acquisition_group ?? "unknown",
    m.medium ?? "unknown",
    m.platform ?? "unknown",
    m.surface ?? "unknown",
    m.campaign_id ?? "",
  ].join(" ");
}

export function legLabel(leg: MarketingLeg): string {
  switch (leg) {
    case "website":
      return "Website";
    case "ctwa":
      return "Meta ad → WhatsApp";
    case "organic":
      return "Organic";
    case "snap":
      return "Snap (Instax booth)";
    case "link":
      return "Tracking link";
    case "launchpad":
      return "Launchpad";
    case "my_web":
      return "My Echoflicks web";
    default:
      return leg;
  }
}

/** Human label for a campaign row — ad/campaign name if resolved, else the token/ad id. */
export function sourceLabel(
  m: Pick<
    MarketingMetric,
    "source" | "campaign_token" | "ad_id" | "campaign_name" | "leg"
  >,
): string {
  if (m.campaign_name) return m.campaign_name;
  if (m.source === "meta_ctwa") {
    return m.ad_id ? `Meta ad ${m.ad_id}` : "Meta ad → WhatsApp";
  }
  if (m.source?.startsWith("website:")) {
    const token = m.source.slice("website:".length);
    if (!token || token === "web" || token === "organic")
      return "Website · Organic";
    if (token === "ig") return "Website · Instagram";
    if (token === "lead_campaign_jul26" || token === "lead_campaign_july26")
      return "Website · Lead campaign July 26";
    return token;
  }
  if (m.source === "snap") return "Snap (Instax booth)";
  if (m.source === "my_web") return "My Echoflicks web";
  if (m.source === "launchpad") return "Launchpad (untagged)";
  if (m.source?.startsWith("launchpad:")) {
    const campaign = m.source.slice("launchpad:".length);
    return campaign ? `Launchpad · ${campaign}` : "Launchpad (untagged)";
  }
  if (m.source?.startsWith("link:")) {
    return m.campaign_name || `Tracking link ${m.source.slice("link:".length)}`;
  }
  if (m.source === "organic") return "Organic";
  return m.campaign_token || m.source || legLabel(m.leg);
}

/** null + null = null; otherwise treat null as 0 so a present value is preserved. */
export function sumNullable(a: number | null, b: number | null): number | null {
  if (a == null && b == null) return null;
  return (a ?? 0) + (b ?? 0);
}

/** Group rollup rows into one summary per campaign, summing every funnel stage. */
export function aggregateByCampaign(
  rows: readonly MarketingMetric[],
): CampaignSummary[] {
  const byKey = new Map<string, CampaignSummary>();
  for (const row of rows) {
    const key = campaignKey(row);
    let summary = byKey.get(key);
    if (!summary) {
      summary = {
        key,
        leg: row.leg,
        source: row.source,
        campaign_token: row.campaign_token ?? "",
        ad_id: row.ad_id ?? "",
        label: sourceLabel(row),
        acquisition_group: row.acquisition_group ?? "unknown",
        medium: row.medium ?? "unknown",
        platform: row.platform ?? "unknown",
        surface: row.surface ?? "unknown",
        campaign_id: row.campaign_id ?? "",
        web_pageviews: 0,
        web_visitors: 0,
        wa_cta_clicks: 0,
        short_link_clicks: 0,
        new_contacts: 0,
        echoes_started: 0,
        echoes_completed: 0,
        purchases: 0,
        spend_cents: null,
        impressions: null,
        link_clicks: null,
        conversations_started: null,
      };
      byKey.set(key, summary);
    }
    // Prefer a resolved campaign name if any row in the group has one.
    if (
      row.campaign_name &&
      summary.label === sourceLabel({ ...row, campaign_name: null })
    ) {
      summary.label = row.campaign_name;
    }
    for (const field of COUNT_FIELDS) {
      summary[field] += row[field] ?? 0;
    }
    for (const field of NULLABLE_FIELDS) {
      summary[field] = sumNullable(summary[field], row[field] ?? null);
    }
  }
  return Array.from(byKey.values()).sort(
    (a, b) =>
      b.new_contacts - a.new_contacts || b.web_visitors - a.web_visitors,
  );
}

export function aggregateTotals(
  rows: readonly MarketingMetric[],
): FunnelTotals {
  return rows.reduce<FunnelTotals>(
    (acc, row) => ({
      web_visitors: acc.web_visitors + (row.web_visitors ?? 0),
      wa_cta_clicks: acc.wa_cta_clicks + (row.wa_cta_clicks ?? 0),
      short_link_clicks: acc.short_link_clicks + (row.short_link_clicks ?? 0),
      new_contacts: acc.new_contacts + (row.new_contacts ?? 0),
      echoes_completed: acc.echoes_completed + (row.echoes_completed ?? 0),
      purchases: acc.purchases + (row.purchases ?? 0),
      spend_cents: sumNullable(acc.spend_cents, row.spend_cents ?? null),
    }),
    {
      web_visitors: 0,
      wa_cta_clicks: 0,
      short_link_clicks: 0,
      new_contacts: 0,
      echoes_completed: 0,
      purchases: 0,
      spend_cents: null,
    },
  );
}

/** New contacts per period, split by leg — feeds the stacked time-series chart. */
export function aggregateByDay(
  rows: readonly MarketingMetric[],
): DailyBucket[] {
  const byPeriod = new Map<string, DailyBucket>();
  for (const row of rows) {
    let bucket = byPeriod.get(row.period);
    if (!bucket) {
      bucket = {
        period: row.period,
        website: 0,
        ctwa: 0,
        organic: 0,
        snap: 0,
        link: 0,
        launchpad: 0,
        my_web: 0,
      };
      byPeriod.set(row.period, bucket);
    }
    bucket[row.leg] += row.new_contacts ?? 0;
  }
  return Array.from(byPeriod.values()).sort((a, b) =>
    a.period < b.period ? -1 : a.period > b.period ? 1 : 0,
  );
}

/** Percentage of `denom` represented by `numer`, or null when undefined. */
export function ratePct(numer: number, denom: number): number | null {
  if (!denom) return null;
  return (numer / denom) * 100;
}

/** Cost per completed echo, in cents. */
export function cpaCents(summary: {
  spend_cents: number | null;
  echoes_completed: number;
}): number | null {
  if (summary.spend_cents == null || summary.echoes_completed <= 0) return null;
  return summary.spend_cents / summary.echoes_completed;
}

export function formatInt(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat().format(value);
}

export function formatPct(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${Math.round(value)}%`;
}

export function formatCents(
  cents: number | null | undefined,
  currency = "EUR",
): string {
  if (cents == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(cents / 100);
  } catch {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }
}
